import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { formatDate, readingTime } from '@/lib/articleUtils';
import type { Article } from '@/hooks/useArticles';

export function ArticleCard({ article }: { article: Article }) {
  const image = article.image ? sanitizeUrl(article.image) : undefined;
  const mins = readingTime(article.event.content);

  return (
    <Card className="group w-full max-w-lg mx-auto overflow-hidden border-border/60 bg-card/60 backdrop-blur-sm transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 p-0">
      <Link
        to={`/article/${encodeURIComponent(article.identifier)}`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
      >
        {image && (
          <div className="relative aspect-square w-full overflow-hidden bg-secondary">
            {/* Blurred fill to gracefully pad the letterbox areas */}
            <img
              src={image}
              alt=""
              aria-hidden
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-xl opacity-40"
            />
            {/* Full image, entirely visible (no cropping) */}
            <img
              src={image}
              alt=""
              loading="lazy"
              className="relative h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
        )}
        <div className="p-5 sm:p-6 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <time dateTime={new Date(article.publishedAt * 1000).toISOString()}>
              {formatDate(article.publishedAt)}
            </time>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {mins} min read
            </span>
          </div>

          <h2 className="font-serif text-xl sm:text-2xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
            {article.title}
          </h2>

          {article.summary && (
            <p className="text-muted-foreground leading-relaxed line-clamp-3">
              {article.summary}
            </p>
          )}

          {article.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {article.hashtags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary/90"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </Card>
  );
}
